import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form } from 'react-bootstrap';
import { FaEdit, FaPlus, FaSave, FaTimes, FaBan, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../services/api';

import { PageHeader }      from '../../components/shared/PageHeader';
import { SearchFilterBar } from '../../components/shared/SearchFilterBar';
import { DataTable }       from '../../components/shared/DataTable';
import { StatusSwitch }    from '../../components/shared/StatusSwitch';

const COLUMNS = ['Nome', 'Duração', 'Unidade', 'Valor (R$)', 'Status', 'Ações'];

// ==========================================
export default function Planos() {
  const [planos,        setPlanos]        = useState([]);
  const [searchTerm,    setSearchTerm]    = useState('');
  const [showModal,     setShowModal]     = useState(false);
  const [currentPlano,  setCurrentPlano]  = useState(null);
  const [loading,       setLoading]       = useState(true);

  // ── Fetch ──
  const fetchPlanos = async () => {
    try {
      setLoading(true);
      const res = await api.get('/media-plans');
      setPlanos(res.data);
    } catch {
      toast.error('Erro ao carregar os planos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlanos(); }, []);

  // ── Handlers ──
  const handleSave = async (data) => {
    try {
      const payload = {
        name:     data.name,
        duration: parseInt(data.duration, 10),
        unit:     data.unit,
        amount:   parseFloat(data.amount),
        isActive: data.isActive,
      };

      if (data.id) {
        await api.put(`/media-plans/${data.id}`, payload);
        toast.success('Plano atualizado com sucesso!');
      } else {
        await api.post('/media-plans', payload);
        toast.success('Plano cadastrado com sucesso!');
      }

      await fetchPlanos();
      setShowModal(false);
      setCurrentPlano(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar o plano.');
    }
  };

  const handleToggleStatus = async (plano) => {
    const msg = plano.isActive
      ? 'Tem certeza que deseja INATIVAR este plano?'
      : 'Deseja ATIVAR este plano novamente?';
    if (!window.confirm(msg)) return;
    try {
      await api.put(`/media-plans/${plano.id}`, {
        name:     plano.name,
        duration: plano.duration,
        unit:     plano.unit,
        amount:   Number(plano.amount),
        isActive: !plano.isActive,
      });
      toast.success(`Plano ${plano.isActive ? 'inativado' : 'ativado'} com sucesso!`);
      await fetchPlanos();
    } catch {
      toast.error('Erro ao alterar o status do plano.');
    }
  };

  // ── Filtro ──
  const filtered = planos.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container fluid className="p-4">

      <PageHeader
        title="Planos de Mídia"
        buttonLabel="Novo Plano"
        onButtonClick={() => { setCurrentPlano(null); setShowModal(true); }}
      />

      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Buscar plano por nome..."
      />

      <DataTable columns={COLUMNS} loading={loading} emptyMessage="Nenhum plano encontrado.">
        {filtered.length > 0 && filtered.map(plano => (
          <tr key={plano.id}>
            <td>{plano.name}</td>
            <td>{plano.duration}</td>
            <td className="text-capitalize">{plano.unit}</td>
            <td>R$ {Number(plano.amount).toFixed(2).replace('.', ',')}</td>
            <td>
              {plano.isActive
                ? <span className="text-success fw-bold"><FaCheckCircle className="me-1" />Ativo</span>
                : <span className="text-secondary fw-bold"><FaBan className="me-1" />Inativo</span>}
            </td>
            <td>
              <Button variant="warning" size="sm" className="me-2"
                onClick={() => { setCurrentPlano(plano); setShowModal(true); }}
                title="Editar Plano">
                <FaEdit />
              </Button>
              <Button
                variant={plano.isActive ? 'danger' : 'success'}
                size="sm"
                onClick={() => handleToggleStatus(plano)}
                title={plano.isActive ? 'Inativar Plano' : 'Ativar Plano'}>
                {plano.isActive ? <FaBan /> : <FaCheckCircle />}
              </Button>
            </td>
          </tr>
        ))}
      </DataTable>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Body>
          <PlanoForm
            plano={currentPlano}
            onSave={handleSave}
            onCancel={() => setShowModal(false)}
          />
        </Modal.Body>
      </Modal>

    </Container>
  );
}

// ==========================================
// FORMULÁRIO
// ==========================================
function PlanoForm({ plano, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    id:       plano?.id       || null,
    name:     plano?.name     || '',
    duration: plano?.duration || '',
    unit:     plano?.unit     || 'meses',
    amount:   plano?.amount   ? Number(plano.amount) : '',
    isActive: plano?.id ? plano.isActive : true,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <Card className="border-0">
      <Card.Header className="bg-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{plano ? 'Editar Plano de Mídia' : 'Novo Plano de Mídia'}</h5>
        <StatusSwitch
          value={formData.isActive}
          onChange={(v) => setFormData(prev => ({ ...prev, isActive: v }))}
        />
      </Card.Header>

      <Card.Body>
        <Form onSubmit={(e) => { e.preventDefault(); onSave(formData); }}>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Nome do Plano *</Form.Label>
                <Form.Control
                  name="name" value={formData.name} onChange={handleChange}
                  placeholder="Ex: Plano Anual Outdoor 20s" required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Duração *</Form.Label>
                <Form.Control
                  type="number" name="duration" value={formData.duration}
                  onChange={handleChange} required min={1}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Unidade *</Form.Label>
                <Form.Select name="unit" value={formData.unit} onChange={handleChange}>
                  <option value="dias">Dias</option>
                  <option value="meses">Meses</option>
                  {/* <option value="anos">Anos</option> */}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Valor (R$) *</Form.Label>
                <Form.Control
                  type="number" step="0.01" name="amount" value={formData.amount}
                  onChange={handleChange} placeholder="0.00" required
                />
              </Form.Group>
            </Col>
          </Row>

          <hr />
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button variant="secondary" onClick={onCancel}>
              <FaTimes className="me-1" /> Cancelar
            </Button>
            <Button variant="success" type="submit">
              <FaSave className="me-2" /> Salvar
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
}
